import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ArrowRight,
  ChevronRight,
  Clock,
  DollarSign,
  Eye,
  Lightbulb,
  Link2,
  Megaphone,
  Package,
  Sparkles,
  SquareCheckBig,
  Tag,
  Truck,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ordersJourneyUrl, ordersPackagesUrl } from '@/lib/ordersApp';
import type { BriefingItem } from '@/api/briefing';

/**
 * Known kinds get an icon/label/color; anything else renders through the
 * generic fallback so the agent is free to introduce new kinds without a
 * frontend change.
 */
export const KIND_CONFIG: Record<string, { icon: typeof Clock; label: string; colorClass: string }> = {
  deadline: { icon: Clock, label: 'Deadlines', colorClass: 'text-danger-ink' },
  action: { icon: SquareCheckBig, label: 'Action Items', colorClass: 'text-info-ink' },
  new_release: { icon: Sparkles, label: 'New Releases', colorClass: 'text-accent2-ink' },
  line_setup: { icon: Wrench, label: 'Line Setup', colorClass: 'text-warning-ink' },
  deal: { icon: Tag, label: 'Supplier Deals', colorClass: 'text-success-ink' },
  competitor: { icon: Megaphone, label: 'Competitor Promos', colorClass: 'text-caution-ink' },
  shipment: { icon: Package, label: 'Incoming Shipments', colorClass: 'text-fresh-ink' },
  insight: { icon: Lightbulb, label: 'Worth Noting', colorClass: 'text-accent1-ink' },
  fyi: { icon: Eye, label: 'FYI', colorClass: 'text-muted-foreground' },
};

export const KIND_ORDER = [
  'deadline',
  'action',
  'new_release',
  'line_setup',
  'shipment',
  'deal',
  'competitor',
  'insight',
  'fyi',
];

export const STALE_AFTER_HOURS = 26;

export function kindSortIndex(kind: string): number {
  const index = KIND_ORDER.indexOf(kind);
  // Unknown kinds slot in just before the fyi tail rather than the very end.
  return index === -1 ? KIND_ORDER.length - 2 + 0.5 : index;
}

export function titleizeKind(kind: string): string {
  return kind
    .split(/[_-]/)
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

export function formatGeneratedAt(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The `data.due` chip has always silently meant four different things
 * (order-by / pay-by / carrier ETA / offer expiry); `data.due_type` makes the
 * semantics explicit so the chip can say which one it is.
 */
const DUE_TYPE_PREFIX: Record<string, string> = {
  due: 'Due',
  order_by: 'Order by',
  pay_by: 'Pay by',
  eta: 'ETA',
  expires: 'Expires',
};

export function formatDueChip(due: string, dueType?: string | null): { text: string; overdue: boolean; today: boolean } {
  const date = parseLocalDate(due.slice(0, 10));
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const overdue = date < startOfToday;
  const today = date.getTime() === startOfToday.getTime();
  const prefix = DUE_TYPE_PREFIX[dueType ?? 'due'] ?? 'Due';
  const short = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (overdue) {
    return {
      text: !dueType || dueType === 'due' ? `Overdue ${date.toLocaleDateString()}` : `${prefix} ${short} — passed`,
      overdue,
      today,
    };
  }
  if (today) return { text: `${prefix} today`, overdue, today };
  return { text: `${prefix} ${short}`, overdue, today };
}

// ---------------------------------------------------------------------------
// Typed views over the loose `data` object. The server validates these shapes
// on PUT (briefingController.validateDataConventions); the guards here are for
// items stored before that validation existed.
// ---------------------------------------------------------------------------

export interface BriefingJourneyData {
  stage: string | null;
  built: number | null;
  orderedLines: number | null;
}

export interface BriefingShipmentData {
  carrier: string | null;
  parcels: number | null;
  eta: string | null;
  status: string | null;
}

export interface BriefingQuoteData {
  text: string;
  who: string | null;
}

export interface BriefingEntryData {
  source: string;
  gist: string;
  action: string | null;
}

const optString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;
const optInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export interface BriefingConventions {
  summaryMd: string | null;
  nextStep: string | null;
  due: string | null;
  dueType: string | null;
  amount: number | null;
  vendor: string | null;
  code: string | null;
  journeyId: number | null;
  journey: BriefingJourneyData | null;
  shipment: BriefingShipmentData | null;
  quote: BriefingQuoteData | null;
  relatedKeys: string[];
  entries: BriefingEntryData[];
}

export function parseBriefingConventions(item: BriefingItem): BriefingConventions {
  const data = item.data ?? {};
  const journeyRaw = data.journey;
  const journey =
    journeyRaw && typeof journeyRaw === 'object' && !Array.isArray(journeyRaw)
      ? {
          stage: optString((journeyRaw as Record<string, unknown>).stage),
          built: optInt((journeyRaw as Record<string, unknown>).built),
          orderedLines: optInt((journeyRaw as Record<string, unknown>).ordered_lines),
        }
      : null;

  const shipmentRaw = data.shipment;
  const shipment =
    shipmentRaw && typeof shipmentRaw === 'object' && !Array.isArray(shipmentRaw)
      ? {
          carrier: optString((shipmentRaw as Record<string, unknown>).carrier),
          parcels: optInt((shipmentRaw as Record<string, unknown>).parcels),
          eta: optString((shipmentRaw as Record<string, unknown>).eta),
          status: optString((shipmentRaw as Record<string, unknown>).status),
        }
      : null;

  const quoteRaw = data.quote;
  const quoteText =
    quoteRaw && typeof quoteRaw === 'object' && !Array.isArray(quoteRaw)
      ? optString((quoteRaw as Record<string, unknown>).text)
      : null;

  const journeyIdRaw = data.journey_id;
  const journeyId =
    typeof journeyIdRaw === 'number'
      ? journeyIdRaw
      : typeof journeyIdRaw === 'string' && /^\d+$/.test(journeyIdRaw)
        ? Number(journeyIdRaw)
        : null;

  return {
    summaryMd: optString(data.summary_md),
    nextStep: optString(data.next_step),
    due: optString(data.due),
    dueType: optString(data.due_type),
    amount: optInt(data.amount),
    vendor: optString(data.vendor),
    code: optString(data.code),
    journeyId,
    journey: journey && (journey.stage || journey.built !== null || journey.orderedLines !== null) ? journey : null,
    shipment:
      shipment && (shipment.carrier || shipment.parcels !== null || shipment.eta || shipment.status)
        ? shipment
        : null,
    quote: quoteText
      ? { text: quoteText, who: optString((quoteRaw as Record<string, unknown>).who) }
      : null,
    relatedKeys: Array.isArray(data.related_keys)
      ? data.related_keys.filter((key): key is string => typeof key === 'string')
      : [],
    entries: Array.isArray(data.entries)
      ? data.entries
          .map((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
            const record = entry as Record<string, unknown>;
            const source = optString(record.source);
            const gist = optString(record.gist);
            return source && gist ? { source, gist, action: optString(record.action) } : null;
          })
          .filter((entry): entry is BriefingEntryData => entry !== null)
      : [],
  };
}

export function BriefingMarkdown({ content, className }: { content: string; className?: string }) {
  const html = useMemo(() => {
    if (!content.trim()) {
      return '';
    }
    const raw = marked.parse(content);
    const normalized = typeof raw === 'string' ? raw : '';
    return DOMPurify.sanitize(normalized);
  }, [content]);

  if (!html) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-sm leading-snug text-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:break-all [&_p]:my-0.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_code]:text-xs [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded',
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** The conventional `data` chips (due / amount / code / vendor); all optional. */
export function BriefingChips({ c }: { c: BriefingConventions }) {
  const dueChip = c.due ? formatDueChip(c.due, c.dueType) : null;

  if (!dueChip && c.amount === null && !c.vendor && !c.code) {
    return null;
  }

  return (
    <>
      {dueChip ? (
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            dueChip.overdue || dueChip.today
              ? 'bg-danger-surface text-danger-ink'
              : 'bg-caution-surface text-caution-ink'
          )}
        >
          <Clock className="h-2.5 w-2.5" />
          {dueChip.text}
        </span>
      ) : null}
      {c.amount !== null ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-success-surface px-1.5 py-0.5 text-[10px] font-medium text-success-ink">
          <DollarSign className="h-2.5 w-2.5" />
          {c.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </span>
      ) : null}
      {c.code ? (
        <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-foreground/80">
          {c.code}
        </span>
      ) : null}
      {c.vendor ? <span className="text-[11px] text-muted-foreground">{c.vendor}</span> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Structured strips — journey and shipment state as chips instead of the
// re-narrated "journey X is still `announced` with 0 ordered lines" prose.
// ---------------------------------------------------------------------------

const JOURNEY_STAGE_CLASSES: Record<string, string> = {
  announced: 'bg-accent2-surface text-accent2-ink',
  ordered: 'bg-info-surface text-info-ink',
  confirmed: 'bg-info-surface text-info-ink',
  invoiced: 'bg-warning-surface text-warning-ink',
  received: 'bg-success-surface text-success-ink',
  balanced: 'bg-success-surface text-success-ink',
};

export function BriefingStructuredChips({ c }: { c: BriefingConventions }) {
  const { journey, shipment, journeyId } = c;
  if (!journey && !shipment) {
    return null;
  }

  const shipmentEtaChip = shipment?.eta && !c.due ? formatDueChip(shipment.eta.slice(0, 10), 'eta') : null;

  // The journey and shipment chips are the briefing's only handles on the
  // orders app, which is a different origin now — so they open a new tab and
  // must not also fire the row's "open the referenced email" click.
  const stopRowClick = (e: MouseEvent) => e.stopPropagation();

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {journeyId !== null ? (
        <a
          href={ordersJourneyUrl(journeyId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopRowClick}
          title={`Open journey ${journeyId} in the Orders Tracker`}
          className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-foreground/70 hover:bg-accent hover:text-foreground hover:underline"
        >
          J{journeyId}
        </a>
      ) : null}
      {journey?.stage ? (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            JOURNEY_STAGE_CLASSES[journey.stage] ?? 'bg-muted text-foreground/70'
          )}
        >
          {journey.stage.replace(/_/g, ' ')}
        </span>
      ) : null}
      {journey?.built !== null && journey?.built !== undefined ? (
        <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground/70">
          {journey.built} built
        </span>
      ) : null}
      {journey?.orderedLines !== null && journey?.orderedLines !== undefined ? (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            journey.orderedLines === 0
              ? 'bg-danger-surface text-danger-ink'
              : 'bg-muted text-foreground/70'
          )}
        >
          {journey.orderedLines} ordered
        </span>
      ) : null}
      {shipment ? (
        <a
          href={ordersPackagesUrl()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopRowClick}
          title="Open packages in the Orders Tracker"
          className="inline-flex items-center gap-1 rounded-full bg-fresh-surface px-1.5 py-0.5 text-[10px] font-medium text-fresh-ink hover:underline"
        >
          <Truck className="h-2.5 w-2.5" />
          {[shipment.carrier, shipment.parcels !== null ? `${shipment.parcels} pkg` : null]
            .filter(Boolean)
            .join(' · ') || 'shipment'}
        </a>
      ) : null}
      {shipment?.status ? (
        <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground/70">
          {shipment.status}
        </span>
      ) : null}
      {shipmentEtaChip ? (
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            shipmentEtaChip.overdue || shipmentEtaChip.today
              ? 'bg-danger-surface text-danger-ink'
              : 'bg-caution-surface text-caution-ink'
          )}
        >
          <Clock className="h-2.5 w-2.5" />
          {shipmentEtaChip.text}
        </span>
      ) : null}
    </div>
  );
}

/** The one imperative for the item — visually distinct so a skim finds it. */
export function BriefingNextStep({ nextStep }: { nextStep: string }) {
  return (
    <div className="mt-1 flex items-start gap-1.5 text-[13px] font-medium text-primary">
      <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{nextStep}</span>
    </div>
  );
}

/** The deciding sentence, verbatim, with attribution — replaces mid-prose italics. */
export function BriefingQuote({ quote }: { quote: BriefingQuoteData }) {
  return (
    <blockquote className="mt-1 border-l-2 border-foreground/20 pl-2 text-[13px] leading-snug text-muted-foreground">
      <span className="italic">“{quote.text}”</span>
      {quote.who ? <span className="ml-1 whitespace-nowrap text-xs">— {quote.who}</span> : null}
    </blockquote>
  );
}

const ENTRIES_PREVIEW_COUNT = 5;

/** Digest rows ({source, gist, action?}) — replaces the bullet-wall fyi digests. */
export function BriefingEntries({ entries }: { entries: BriefingEntryData[] }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = entries.length > ENTRIES_PREVIEW_COUNT + 1;
  const visible = overflow && !expanded ? entries.slice(0, ENTRIES_PREVIEW_COUNT) : entries;

  return (
    <div className="mt-1 space-y-0.5">
      {visible.map((entry, index) => (
        <div key={index} className="flex flex-wrap items-baseline gap-x-1.5 text-[13px] leading-snug">
          <span className="font-medium text-foreground/90">{entry.source}</span>
          <span className="min-w-0 text-muted-foreground">{entry.gist}</span>
          {entry.action ? (
            <span className="inline-flex items-center rounded-full bg-warning-surface px-1.5 py-px text-[10px] font-medium text-warning-ink">
              {entry.action}
            </span>
          ) : null}
        </div>
      ))}
      {overflow ? (
        <button
          type="button"
          className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {expanded ? 'Show fewer' : `Show ${entries.length - ENTRIES_PREVIEW_COUNT} more`}
        </button>
      ) : null}
    </div>
  );
}

export interface BriefingRelatedRef {
  key: string;
  title: string;
}

/** "See also" links to other live items (data.related_keys, resolved upstream). */
export function BriefingRelated({
  related,
  onOpenRelated,
}: {
  related: BriefingRelatedRef[];
  onOpenRelated?: (key: string) => void;
}) {
  if (related.length === 0) {
    return null;
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {related.map((ref) => (
        <button
          key={ref.key}
          type="button"
          disabled={!onOpenRelated}
          className="inline-flex max-w-56 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground/70 transition-colors enabled:hover:bg-muted/70 enabled:hover:text-foreground"
          title={ref.title}
          onClick={(e) => {
            e.stopPropagation();
            onOpenRelated?.(ref.key);
          }}
        >
          <Link2 className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{ref.title}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * The collapsed evidence section: body_md when a summary exists. The audit
 * trail (verbatim quotes, verification provenance, recorded traps) lives here
 * — present when needed, out of the skim path by default.
 */
export function BriefingDetails({
  bodyMd,
  defaultOpen = false,
}: {
  bodyMd: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-1">
      <button
        type="button"
        className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} />
        Details
      </button>
      {open ? (
        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
          <BriefingMarkdown content={bodyMd} />
        </div>
      ) : null}
    </div>
  );
}

const LEGACY_CLAMP_THRESHOLD_CHARS = 700;

/**
 * Pre-format-split items: a body with no summary renders in full, but long
 * ones clamp with a fade + toggle so a single legacy essay can't repaint the
 * panel as a wall of text.
 */
export function BriefingLegacyBody({ bodyMd, fullDetail }: { bodyMd: string; fullDetail: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const clamped = !fullDetail && !expanded && bodyMd.length > LEGACY_CLAMP_THRESHOLD_CHARS;

  return (
    <div className="mt-1">
      <div className={cn(clamped && 'relative max-h-40 overflow-hidden')}>
        <BriefingMarkdown content={bodyMd} />
        {clamped ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent" />
        ) : null}
      </div>
      {!fullDetail && bodyMd.length > LEGACY_CLAMP_THRESHOLD_CHARS ? (
        <button
          type="button"
          className="mt-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}
